/**
 * Structured article body: ordered sections (legacy graph blocks preserved
 * as JSON instead of flattened Markdown).
 */

import { stripInlineMarkdown } from '@/lib/article-inline-format';

export const ARTICLE_SECTION_TYPES = [
    'paragraph',
    'title',
    'quote',
    'video',
    'link',
    'image',
    'gallery',
    'map',
] as const;

export type ArticleSectionType = (typeof ARTICLE_SECTION_TYPES)[number];

export type ParagraphSection = { type: 'paragraph'; content: string };
export type TitleSection = { type: 'title'; title: string };
export type QuoteSection = { type: 'quote'; quote: string; speaker?: string };
export type VideoSection = {
    type: 'video';
    url: string;
    caption?: string;
    scale?: string;
};
export type LinkSection = { type: 'link'; url: string; label: string };
/** Inline image: references a row in the `images` gallery table. */
export type ImageSection = {
    type: 'image';
    imageId: string;
    /** Optional per-article overrides; fall back to the images row. */
    caption?: string;
    credit?: string;
};
/** Inline grid of images; wider than prose on the article page. */
export type GallerySection = {
    type: 'gallery';
    imageIds: string[];
    caption?: string;
};
export type MapSection = { type: 'map'; address: string; caption?: string };

export type ArticleSection =
    | ParagraphSection
    | TitleSection
    | QuoteSection
    | VideoSection
    | LinkSection
    | ImageSection
    | GallerySection
    | MapSection;

const SECTION_TYPE_SET = new Set<string>(ARTICLE_SECTION_TYPES);

/**
 * Word / Google Docs paste often inserts NBSP (U+00A0) between every word.
 * Browsers treat that as non-wrapping, so a whole paragraph becomes one
 * horizontal line and blows the editor / article layout.
 */
export function normalizeArticleText(value: string): string {
    return value
        .replace(/\u00a0/g, ' ')
        .replace(/\u202f/g, ' ')
        .replace(/\u2007/g, ' ');
}

function s(v: unknown): string {
    if (v === null || v === undefined) return '';
    return normalizeArticleText(String(v)).trim();
}

/**
 * Split a paragraph section into real paragraphs.
 *
 * - If the text has blank lines, those are the boundaries. Single newlines
 *   inside a block are soft wraps - collapse them to spaces so we do not turn
 *   every line into a stacked pseudo-paragraph.
 * - Legacy imports often used a single Enter between paragraphs and never
 *   stored a blank line; in that case each non-empty line is its own paragraph.
 */
export function splitParagraphContent(content: string): string[] {
    if (!content) return [];
    const normalized = normalizeArticleText(content)
        .replace(/\r\n/g, '\n')
        .trim();
    if (!normalized) return [];

    if (/\n\s*\n/.test(normalized)) {
        return normalized
            .split(/\n\s*\n/)
            .map((block) =>
                block.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim(),
            )
            .filter(Boolean);
    }

    return normalized
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
}

export type ParseArticleBodyOptions = {
    /**
     * When false, keep inline newlines inside paragraph / quote strings.
     * Use for TipTap round-trip; DB / legacy imports keep the default true.
     */
    splitParagraphLines?: boolean;
};

/** Normalize unknown JSON (DB / admin / migration) into a clean section list. */
export function parseArticleBody(
    raw: unknown,
    options: ParseArticleBodyOptions = {},
): ArticleSection[] {
    const splitParagraphLines = options.splitParagraphLines ?? true;
    if (raw == null) return [];
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return [];
        try {
            return parseArticleBody(JSON.parse(trimmed));
        } catch {
            // Legacy Markdown blob: expand blank-line / single-Enter runs into
            // real paragraph sections so the site still renders correctly.
            return splitParagraphContent(trimmed).map((content) => ({
                type: 'paragraph' as const,
                content,
            }));
        }
    }
    if (!Array.isArray(raw)) return [];

    const out: ArticleSection[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const block = item as Record<string, unknown>;
        const type = s(block.type);
        if (!SECTION_TYPE_SET.has(type)) continue;

        switch (type as ArticleSectionType) {
            case 'paragraph': {
                const content = normalizeArticleText(String(block.content ?? ''));
                if (splitParagraphLines) {
                    // One DB block may hold several paragraphs joined by \n\n
                    // (or legacy single-\n). Expand to one section each.
                    for (const part of splitParagraphContent(content)) {
                        out.push({ type: 'paragraph', content: part });
                    }
                } else {
                    const trimmed = content.trim();
                    if (trimmed) out.push({ type: 'paragraph', content: trimmed });
                }
                break;
            }
            case 'title': {
                const title = s(block.title ?? block.content);
                if (title) out.push({ type: 'title', title });
                break;
            }
            case 'quote': {
                const quote = splitParagraphLines
                    ? s(block.quote ?? block.content)
                    : normalizeArticleText(
                          String(block.quote ?? block.content ?? ''),
                      ).trim();
                if (!quote) break;
                const speaker = s(block.speaker ?? block.author);
                out.push(
                    speaker
                        ? { type: 'quote', quote, speaker }
                        : { type: 'quote', quote },
                );
                break;
            }
            case 'video': {
                const url = s(block.url);
                if (!url) break;
                const caption = s(block.caption);
                const scale = s(block.scale);
                out.push({
                    type: 'video',
                    url,
                    ...(caption ? { caption } : {}),
                    ...(scale ? { scale } : {}),
                });
                break;
            }
            case 'link': {
                const url = s(block.url);
                if (!url) break;
                out.push({
                    type: 'link',
                    url,
                    label: s(block.label) || url,
                });
                break;
            }
            case 'image': {
                const imageId = s(block.imageId ?? block.image_id);
                if (!imageId) break;
                const caption = s(block.caption);
                const credit = s(block.credit);
                out.push({
                    type: 'image',
                    imageId,
                    ...(caption ? { caption } : {}),
                    ...(credit ? { credit } : {}),
                });
                break;
            }
            case 'gallery': {
                const rawIds = block.imageIds ?? block.image_ids;
                const imageIds = Array.isArray(rawIds)
                    ? [...new Set(rawIds.map((id) => s(id)).filter(Boolean))]
                    : [];
                const caption = s(block.caption);
                out.push({
                    type: 'gallery',
                    imageIds,
                    ...(caption ? { caption } : {}),
                });
                break;
            }
            case 'map': {
                const address = s(block.address);
                if (!address) break;
                const caption = s(block.caption);
                out.push({
                    type: 'map',
                    address,
                    ...(caption ? { caption } : {}),
                });
                break;
            }
        }
    }
    return out;
}

/** Plain text for cards, SEO, reading time, and embeddings. */
export function articleBodyPlainText(sections: ArticleSection[]): string {
    const parts: string[] = [];
    for (const section of sections) {
        switch (section.type) {
            case 'paragraph':
                parts.push(stripInlineMarkdown(section.content));
                break;
            case 'title':
                parts.push(stripInlineMarkdown(section.title));
                break;
            case 'quote':
                parts.push(
                    section.speaker
                        ? `${stripInlineMarkdown(section.quote)} - ${section.speaker}`
                        : stripInlineMarkdown(section.quote),
                );
                break;
            case 'video':
                parts.push(section.caption || section.url);
                break;
            case 'link':
                parts.push(section.label || section.url);
                break;
            case 'image':
                if (section.caption) parts.push(section.caption);
                break;
            case 'gallery':
                if (section.caption) parts.push(section.caption);
                break;
            case 'map': {
                parts.push(
                    section.caption
                        ? `${section.caption}: ${section.address}`
                        : section.address,
                );
                break;
            }
        }
    }
    return parts.join('\n\n').replace(/\s+/g, ' ').trim();
}

export function articleBodyWordCount(sections: ArticleSection[]): number {
    const text = articleBodyPlainText(sections);
    if (!text) return 0;
    return text.split(/\s+/).filter(Boolean).length;
}

export function emptySection(type: ArticleSectionType): ArticleSection {
    switch (type) {
        case 'paragraph':
            return { type: 'paragraph', content: '' };
        case 'title':
            return { type: 'title', title: '' };
        case 'quote':
            return { type: 'quote', quote: '', speaker: '' };
        case 'video':
            return { type: 'video', url: '', caption: '' };
        case 'link':
            return { type: 'link', url: '', label: '' };
        case 'image':
            return { type: 'image', imageId: '' };
        case 'gallery':
            return { type: 'gallery', imageIds: [] };
        case 'map':
            return { type: 'map', address: '', caption: '' };
    }
}

const YOUTUBE_VIDEO_ID_RE = /^[\w-]{11}$/;

const YOUTUBE_HOSTS = new Set([
    'youtube.com',
    'm.youtube.com',
    'music.youtube.com',
    'youtu.be',
    'youtube-nocookie.com',
]);

/** Extract an 11-char YouTube video id from any common YouTube URL, or null. */
export function youtubeVideoId(rawUrl: string): string | null {
    const trimmed = rawUrl.trim();
    if (!trimmed) return null;

    let parsed: URL;
    try {
        parsed = new URL(
            /^(https?:)?\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
        );
    } catch {
        return null;
    }

    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (!YOUTUBE_HOSTS.has(host)) return null;

    let id = '';
    if (host === 'youtu.be') {
        id = parsed.pathname.split('/').filter(Boolean)[0] ?? '';
    } else {
        id = parsed.searchParams.get('v') ?? '';
        if (!id) {
            const match = parsed.pathname.match(
                /\/(?:embed|shorts|live|v|e)\/([^/?#]+)/,
            );
            id = match?.[1] ?? '';
        }
    }

    id = id.split(/[?&]/)[0] ?? '';
    return YOUTUBE_VIDEO_ID_RE.test(id) ? id : null;
}

/** YouTube embed URL, or null when the input is not a valid YouTube link. */
export function videoEmbedUrl(rawUrl: string): string | null {
    const id = youtubeVideoId(rawUrl);
    return id ? `https://www.youtube.com/embed/${id}` : null;
}

/** Maps embed that does not require a Maps JavaScript API key. */
export function mapEmbedUrl(address: string): string {
    return (
        'https://maps.google.com/maps?q=' +
        encodeURIComponent(address) +
        '&z=13&hl=iw&output=embed'
    );
}
